import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";

/**
 * Data hooks for the Allsale Rewards (in-store loyalty) flow. Every
 * endpoint is JWT-authenticated; consumers should be inside <RequireAuth>.
 * Mutations invalidate `["rewards-me"]` so the balance / codes / ledger
 * refresh automatically after any successful action.
 */

const ME_KEY = ["rewards-me"];
const STORES_KEY = ["rewards-stores"];

export function useRewardsStores(coords = null) {
  return useQuery({
    // Iter-352 — optional ?lat&lng: rows gain distance_km and come back
    // nearest-first. Without coords the response is A–Z as before.
    queryKey: coords ? [...STORES_KEY, coords.lat, coords.lng] : STORES_KEY,
    queryFn: async () => {
      const { data } = await sharedApi.get("/rewards/stores", {
        params: coords ? { lat: coords.lat, lng: coords.lng } : undefined,
      });
      return Array.isArray(data) ? data : data?.stores || [];
    },
    staleTime: 5 * 60_000,
  });
}

export function useRewardsMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: async () => (await sharedApi.get("/rewards/me")).data,
    staleTime: 15_000,
  });
}

// Iter-356 — Visit Streaks (display only, no points). Server is the
// source of truth: badge string ("🔥 n visits") comes pre-baked, null
// under 2 distinct NZ-time visit days at the same store.
export function useRewardsStreak(enabled = true) {
  return useQuery({
    queryKey: ["rewards-streak"],
    enabled,
    retry: 0,
    staleTime: 60_000,
    queryFn: async () => (await sharedApi.get("/rewards/streak")).data,
  });
}

function useMutateWithMeRefresh(mutationFn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_KEY }),
  });
}

export function useCheckin() {
  return useMutateWithMeRefresh(async (qrToken) => {
    const token = String(qrToken || "").trim();
    const { data } = await sharedApi.post("/rewards/checkin", { qr_token: token });
    return data;
  });
}

export function useReviewProof() {
  return useMutateWithMeRefresh(async ({ store_id, platform, screenshot_url }) => {
    const { data } = await sharedApi.post("/rewards/review-proof", {
      store_id,
      platform,
      screenshot_url,
    });
    return data;
  });
}

export function useRedeem() {
  return useMutateWithMeRefresh(async ({ store_id, points }) => {
    const { data } = await sharedApi.post("/rewards/redeem", {
      store_id,
      points: Number(points),
    });
    return data;
  });
}
